import { observable, action } from "mobx";
import { SBMsgBus } from "sb-msg-bus";
import { SelectionModel, ErrorModel, PurchaseModel, BetModel, StakeButtonsStatusesModel } from "../models";
import { ErrorType, BetStatus } from "../enums";

import {
    YourBetStake,
    YourBetTopic,
    YourBetError,
    YourBetActions,
    YourBetPurchase,
    YourBetSelection,
    YourBetPurchaseInfo,
    YourBetPlacedPurchase,
    YourBetPlaceBetResponse,
} from "sb-resp-lib/lib/MessageBusTopics/YourBet";

export class Retriever {
    private messageBus: SBMsgBus;
    @observable betStatus: number;
    @observable bets: BetModel[];
    @observable errors: ErrorModel[];
    @observable purchases: PurchaseModel[];
    @observable liveSelections: SelectionModel[];
    @observable preliveSelections: SelectionModel[];
    @observable stakeButtonsStatus: StakeButtonsStatusesModel | any; //added any as an option of updatedStatus type because jest doesn't cover it
    @observable isPlaceBetButtonEnabled: boolean;

    constructor(msgBus: SBMsgBus) {
        this.messageBus = msgBus;
        this.betStatus = BetStatus.Default;
        this.bets = [];
        this.errors = [];
        this.purchases = [];
        this.liveSelections = [];
        this.preliveSelections = [];
        this.isPlaceBetButtonEnabled = false;
        this.stakeButtonsStatus = Object.create({});
        this.getData = this.getData.bind(this);
        this.onMsgBusTopicReceived = this.onMsgBusTopicReceived.bind(this);
    }

    activate() {
        this.getData();
    }

    @action.bound deactivate() {
        this.bets = [];
        this.errors = [];
        this.purchases = [];
        this.stakeButtonsStatus = Object.create({});
        this.clearSelections();
        this.messageBus.unsubscribe("sb_yourbet_topic", this.onMsgBusTopicReceived);
    }

    getData() {
        this.messageBus.subscribe("sb_yourbet_topic", this.onMsgBusTopicReceived);
    }

    onMsgBusTopicReceived(topic: YourBetTopic) {
        if (topic) {
            Object.keys(topic).forEach(x => {
                let funcName = `${x}Topic`;
                if (this.checkIfFunction(funcName)) {
                    this[funcName](topic[x]);
                }
            }, this);
        }
    }

    @action.bound actionsTopic(actions: YourBetActions) {
        Object.keys(actions).forEach(x => {
            let funcName = `${x}Action`;
            if (this.checkIfFunction(funcName)) {
                this[funcName](actions[x]);
            }
        }, this);
    }

    @action.bound purchaseAction(purchases: YourBetPurchase[]) {
        this.purchases = this.toPurchaseModels(purchases);
    }

    @action.bound errorAction(errors: YourBetError[]) {
        this.errors = this.toErrorModels(errors);
    }

    placeBet(stake: string, selections: SelectionModel[]) {
        let groupedSelections = this.getGroupedSelections(selections);
        let yourBetSelections: YourBetSelection[] = groupedSelections.live.concat(groupedSelections.prelive);
        let message = {
            actions: {
                place: {
                    stake,
                    selections: yourBetSelections
                }
            }
        }
        this.messageBus.post({ sb_yourbet_topic: message });
        return message;
    }

    updateNewOfferStatus(waitingBetId: string, isAccepted: boolean) {
        let message = {
            actions: {
                newOfferStatus: {
                    waitingBetId,
                    isAccepted
                }
            }
        }

        this.messageBus.post({ sb_yourbet_topic: message });
        return message;
    }

    updateOddsChangeStatus(isAccepted: boolean) {
        let message = {
            actions: {
                oddsChangedStatus: {
                    isOddAccepted: isAccepted
                }
            }
        }

        this.messageBus.post({ sb_yourbet_topic: message });
        return message;
    }

    @action.bound placeResultTopic(placeBetResponse: YourBetPlaceBetResponse) {
        this.purchaseResponse(placeBetResponse.purchase);
        this.selectionsResponse(placeBetResponse.selections, placeBetResponse.purchase);
        if (placeBetResponse.error) {
            this.errorResponse(placeBetResponse.error);
        }
    };

    @action.bound purchaseResponse(purchases: YourBetPlacedPurchase[]) {
        this.bets = this.getBets(purchases);
        this.betStatus = this.getPurchaseStatus(purchases);
        this.purchases = this.toPurchaseModels(purchases);
    }

    @action.bound selectionsResponse(selections: YourBetSelection[], purchases: YourBetPlacedPurchase[]) {
        purchases.forEach(x => {
            if (x.liveSelectionIds) {
                let liveSelectionsToReplace = this.mapSelectionsToIds(x.liveSelectionIds, selections, true);
                if (liveSelectionsToReplace.length > 0) {
                    this.setCurrentSelections(liveSelectionsToReplace, true);
                }
            }
            if (x.preliveSelectionIds) {
                let preliveSelectionsToReplace = this.mapSelectionsToIds(x.preliveSelectionIds, selections, false);
                if (preliveSelectionsToReplace.length > 0) {
                    this.setCurrentSelections(preliveSelectionsToReplace, false);
                }
            }
        });
    }

    @action.bound errorResponse(errors: YourBetError[]) {
        this.errors = this.toErrorModels(errors);
    }

    @action.bound selectionsTopic(selections: any) {
        Object.keys(selections).forEach(x => {
            if (x === "live") {
                Object.keys(selections[x].selections).forEach(y => {
                    let funcName = `${y}SelectionsResponse`;
                    if (this.checkIfFunction(funcName)) {
                        this[funcName](selections[x].selections[y], true)
                    }
                }, this);
            }
            else {
                Object.keys(selections[x].selections).forEach(y => {
                    let funcName = `${y}SelectionsResponse`;
                    if (this.checkIfFunction(funcName)) {
                        this[funcName](selections[x].selections[y], false)
                    }
                }, this);
            }
        }, this);
    }

    @action.bound addSelectionsResponse(selectionsToAdd: YourBetSelection[], isLive: boolean) {
        if (selectionsToAdd && selectionsToAdd.length > 0) {
            let currentSelections = this.getCurrentSelections(isLive)
                .concat(selectionsToAdd.map(x => ({ ...x, ...{ isLive } })));
            this.setCurrentSelections(currentSelections, isLive);
        }
    }

    @action.bound updateSelectionsResponse(selectionsToUpdate: YourBetSelection[], isLive: boolean) {
        if (selectionsToUpdate && selectionsToUpdate.length > 0) {
            let currentSelections = this.getCurrentSelections(isLive);
            selectionsToUpdate.forEach(x => {
                let indexToUpdate = currentSelections.findIndex(y => y.id === x.id);
                if (indexToUpdate >= 0) {
                    currentSelections[indexToUpdate] = { ...x, ...{ isLive } };
                }
            })
        }
    }

    @action.bound removeSelectionsResponse(selectionsToRemove: YourBetSelection[], isLive: boolean) {
        if (selectionsToRemove && selectionsToRemove.length > 0) {
            let currentSelections = this.getCurrentSelections(isLive);
            currentSelections = currentSelections.reduce((a: SelectionModel[], b) => {
                let selection = selectionsToRemove.find(y => y.id === b.id);
                if (!selection) {
                    a.push(b);
                }
                return a;
            }, []);
            this.setCurrentSelections(currentSelections, isLive);
        }
    }

    removeSelectionsRequest(selections: SelectionModel[]) {
        let result = this.getRemoveSelectionsRequestMessage({ live: [], prelive: [] });
        if (selections && selections.length > 0) {
            let groupedSelections = this.getGroupedSelections(selections);
            let message = this.getRemoveSelectionsRequestMessage(groupedSelections);
            this.messageBus.post({ sb_yourbet_topic: message });
            result = message;
        }
        return result;
    }

    @action.bound retainPurchaseRequest(purchaseId: string, purchaseViewKey: number) {
        let message: YourBetTopic = {
            actions: {
                retainPurchase: {
                    purchaseId,
                    purchaseViewKey
                }
            }
        };
        this.messageBus.post({ sb_yourbet_topic: message });
        this.clearSelections();
        return message;
    }

    @action.bound clearPurchaseRequest(purchaseId: string, purchaseViewKey: number) {
        let message: YourBetTopic = {
            actions: {
                clearPurchase: {
                    purchaseId,
                    purchaseViewKey
                }
            }
        };
        this.messageBus.post({ sb_yourbet_topic: message });
        this.clearSelections();
        return message;
    }

    @action.bound clearAllPurchases(purchases: PurchaseModel[]) {
        let purchasesResult: YourBetPurchaseInfo[] = purchases.reduce((a: YourBetPurchaseInfo[], purchase) => {
            if (purchase.id && purchase.viewKey) {
                let currentPurchase: YourBetPurchaseInfo = {
                    purchaseId: purchase.id,
                    purchaseViewKey: purchase.viewKey
                }
                a.push(currentPurchase);
            }
            return a;
        }, []);

        let message: YourBetTopic = {
            actions: {
                clearAllPurchases: purchasesResult
            }
        };
        this.messageBus.post({ sb_yourbet_topic: message });
        this.clearSelections();
        return message;
    }

    @action.bound clearSelections() {
        let message = this.getRemoveSelectionsRequestMessage({ live: this.liveSelections, prelive: this.preliveSelections });
        this.messageBus.post({ sb_yourbet_topic: message });
        this.betStatus = BetStatus.Default;
        this.liveSelections = [];
        this.preliveSelections = [];
        return message;
    }

    @action.bound clearPurchaseAction(purchaseInfo: YourBetPurchaseInfo) {
        this.purchases = this.purchases.reduce((a: PurchaseModel[], purchase) => {
            if (purchase.viewKey && purchase.viewKey !== purchaseInfo.purchaseViewKey) {
                a.push(purchase);
            }
            return a;
        }, []);
        this.betStatus = BetStatus.Default;
        this.liveSelections = [];
        this.preliveSelections = [];
    }

    changeStake(stake: string, buttonAction?: string) {
        let stakeMessage = { value: stake };
        if (buttonAction) {
            stakeMessage = {
                value: stake,
                [buttonAction]: true
            }
        }

        let message = {
            actions: {
                stake: stakeMessage as YourBetStake
            }
        }

        this.messageBus.post({ sb_yourbet_topic: message });
        return message;
    }

    @action.bound stakeButtonsStatesAction(obj: any) {
        this.stakeButtonsStatus = new StakeButtonsStatusesModel({
            plusEnabled: obj.plusEnabled || false,
            minusEnabled: obj.minusEnabled || false
        });

        return this.stakeButtonsStatus;
    }

    @action.bound placeBetButtonStateAction(obj: any) {
        this.isPlaceBetButtonEnabled = obj.isEnabled || false;

        return this.isPlaceBetButtonEnabled;
    }

    private getGroupedSelections(selections: SelectionModel[]) {
        let groupedSelections: { live: YourBetSelection[], prelive: YourBetSelection[] } =
            selections.reduce((grouped: { live: YourBetSelection[], prelive: YourBetSelection[] }, selection) => {
                let currentSelection = {
                    id: selection.id,
                    name: selection.name,
                    viewKey: selection.viewKey,
                    eventType: selection.eventType,
                    suspended: selection.suspended
                };

                if (selection.isLive) {
                    grouped.live.push(currentSelection);
                }
                else {
                    grouped.prelive.push(currentSelection);
                }
                return grouped;
            }, { live: [], prelive: [] });
        return groupedSelections;
    }

    private getRemoveSelectionsRequestMessage(groupedSelections: { live: YourBetSelection[], prelive: YourBetSelection[] }) {
        return {
            selections: {
                live: {
                    selections: {
                        remove: groupedSelections.live
                    }
                },
                prelive: {
                    selections: {
                        remove: groupedSelections.prelive
                    }
                }
            }
        }
    }

    private getBets(purchases: YourBetPlacedPurchase[]) {
        let result: BetModel[] = [];
        purchases.forEach(purchase => {
            if (purchase && purchase.bet && purchase.bet.id) {
                let betToAdd = new BetModel({ id: purchase.bet.id });
                result.push(betToAdd);
            }
        });
        return result;
    }

    private getPurchaseStatus(purchases: YourBetPlacedPurchase[]) {
        let resultStatus = 0;
        purchases.forEach(purchase => {
            if (purchase && purchase.status) {
                Object.keys(purchase.status).forEach(key => {
                    if (purchase.status[key]) {
                        resultStatus = this.getStatus(key);
                    }
                }, this);
            }
        }, this);
        return resultStatus;
    }

    private getStatus(statusKey: string) {
        const status = {
            accepted: BetStatus.Accepted,
            rejected: BetStatus.Rejected,
            waiting: BetStatus.Waiting,
            newOffer: BetStatus.NewOffer
        };

        return status[statusKey] || BetStatus.Default;
    }

    private toPurchaseModels(purchases: YourBetPurchase[]) {
        let newPurchases = purchases.reduce((result: PurchaseModel[], purchase) => {
            let indexToUpdate = this.purchases.findIndex(y => y.id === purchase.id);
            if (indexToUpdate !== -1) {
                let purchaseToUpdate = { ...this.purchases[indexToUpdate] };
                Object.keys(purchase).forEach(key => {
                    if (purchase[key] !== undefined && purchase[key] !== null) {
                        purchaseToUpdate[key] = purchase[key];
                    }
                });
                result.push(purchaseToUpdate);
            }
            else {
                result.push(purchase);
            }
            return result;
        }, []);

        return newPurchases;
    }

    private mapSelectionsToIds(selectionIds: string[], selections: YourBetSelection[], isLive: boolean) {
        let resultSelections: SelectionModel[] = []
        selectionIds.forEach(x => {
            let selection = selections.find(y => y.id === x);
            if (selection) {
                resultSelections.push({ ...selection, ...{ isLive } });
            }
        });
        return resultSelections;
    }

    private toErrorModels(errors: YourBetError[]) {
        let errorResult = errors.reduce((result: ErrorModel[], error) => {
            let errorModel = new ErrorModel({});
            Object.keys(error).forEach(key => {
                if (key !== ErrorType.ActivationError
                    && key !== ErrorType.DeactivationError) {
                    errorModel[key] = error[key]
                }
            });
            result.push(errorModel);
            return result;
        }, []);

        return errorResult;
    }

    @action.bound private setCurrentSelections(newSelections: SelectionModel[], isLive: boolean) {
        if (isLive) {
            this.liveSelections = newSelections;
        }
        else {
            this.preliveSelections = newSelections;
        }
    }

    private getCurrentSelections(isLive: boolean): SelectionModel[] {
        if (isLive) {
            return this.liveSelections;
        }
        else {
            return this.preliveSelections;
        }
    }

    private checkIfFunction(functionName: string) {
        if (this[functionName] && typeof this[functionName] === "function") {
            return true;
        }
        else {
            return false;
        }
    }
}